import React, {
  forwardRef,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import classnames from 'clsx';
import { formatClassNames } from '@wix/editor-elements-common-utils';
import { usePopper } from '../../../../../../providers/usePopper/usePopper';
import { useClickOutside } from '../../../../../../providers/useClickOutside/useClickOutside';
import styles from '../button.scss';
import StylesContext from '../../../StylesContext';
import { TestIds } from '../../../../constants';
import semanticClassNames from '../../../../RichTextBox.semanticClassNames';
import AlignButton from './AlignButton';
import type { AlignButtonType } from './common';
import {
  alignmentButtons,
  getActiveAlignment,
  getStateWithNewTextAlign,
  alignTypes,
} from './common';
import type { ExtendedToolbarChildrenProps } from '../../types';
import Tooltip from '../Tooltip';
import { useActionTranslations } from '../../../useActionTranslations';
import { calculateNextIndex, calculatePreviousIndex } from '../../utils';

const StaticTextAlignButton = forwardRef<
  HTMLButtonElement,
  ExtendedToolbarChildrenProps
>((props, ref) => {
  const { setEditorState, getEditorState, rootId, translations, tabIndex } =
    props;

  const [showOptions, setShowOptions] = useState(false);
  const leftAlignRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const alignButtonRefs = useRef<
    Record<AlignButtonType, React.RefObject<HTMLButtonElement>>
  >({
    left: useRef<HTMLButtonElement>(null),
    center: useRef<HTMLButtonElement>(null),
    right: useRef<HTMLButtonElement>(null),
    justify: useRef<HTMLButtonElement>(null),
  });
  const {
    toolbarAlignLeftAction,
    toolbarAlignRightAction,
    toolbarAlignJustifyAction,
    toolbarAlignCenterAction,
  } = useActionTranslations(props.translations);

  const alignActions: Record<AlignButtonType, string> = {
    left: toolbarAlignLeftAction,
    right: toolbarAlignRightAction,
    center: toolbarAlignCenterAction,
    justify: toolbarAlignJustifyAction,
  };

  const alignSelected: Record<AlignButtonType, string> = {
    left: translations.alignLeftSelected,
    right: translations.alignRightSelected,
    center: translations.alignCenterSelected,
    justify: translations.alignJustifySelected,
  };

  const {
    ref: popperSourceElem,
    setRef: setPopperSourceElem,
    setPopper: setPopperTargetElem,
    styles: stylesPopper,
    attributes,
  } = usePopper<HTMLButtonElement>({
    placement: 'bottom',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, -25],
        },
      },
    ],
  });

  const stylesContext = useContext(StylesContext);
  const hideOptions = () => {
    setShowOptions(false);
  };

  useClickOutside([popperSourceElem], hideOptions);

  const changeTextAlign = (
    e: React.MouseEvent<HTMLOrSVGElement>,
    newAlignment: AlignButtonType,
  ) => {
    e.stopPropagation();

    const newState = getStateWithNewTextAlign(newAlignment, getEditorState());
    setEditorState(newState);
    hideOptions();
    popperSourceElem?.focus();
  };

  const onClickHandler = (event: React.MouseEvent<any>) => {
    if (event.currentTarget === popperSourceElem) {
      setShowOptions(!showOptions);
    }
  };

  const handleBlur = (event: React.FocusEvent) => {
    const relatedTarget = event.relatedTarget;

    if (
      relatedTarget &&
      (menuRef.current?.contains(relatedTarget as Node) ||
        popperSourceElem === relatedTarget)
    ) {
      return;
    }

    hideOptions();
  };

  const activeAlignment = getActiveAlignment(getEditorState());

  useEffect(() => {
    if (!showOptions) {
      return;
    }
    alignTypes.forEach(type => {
      const button = alignButtonRefs.current[type].current;
      if (button) {
        button.tabIndex = -1;
      }
    });

    const activeButton = alignButtonRefs.current[activeAlignment].current;
    if (activeButton) {
      activeButton.tabIndex = 0;
      activeButton.focus();
    }
  }, [showOptions, activeAlignment]);

  const onKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (!showOptions) {
      return;
    }

    const buttons = alignTypes.map(
      type => alignButtonRefs.current[type].current as HTMLButtonElement,
    );

    if (event.key === 'Escape') {
      setShowOptions(false);
      popperSourceElem?.focus();
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();

      const currentIndex = Array.from(buttons).findIndex(
        button => button === document.activeElement,
      );
      const nextIndex = calculateNextIndex(currentIndex, buttons.length);

      buttons.forEach((btn, idx) => {
        btn.tabIndex = idx === nextIndex ? 0 : -1;
      });

      (buttons[nextIndex] as HTMLButtonElement).focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();

      const currentIndex = Array.from(buttons).findIndex(
        button => button === document.activeElement,
      );
      const nextIndex = calculatePreviousIndex(currentIndex, buttons.length);

      buttons.forEach((btn, idx) => {
        btn.tabIndex = idx === nextIndex ? 0 : -1;
      });

      buttons[nextIndex].focus();
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.stopPropagation();
    }
  };

  const onKeyDownMenu = (event: React.KeyboardEvent<HTMLElement>) => {
    if (
      !showOptions &&
      (event.key === 'ArrowDown' ||
        event.key === 'ArrowUp' ||
        event.key === 'Enter' ||
        event.key === ' ')
    ) {
      event.preventDefault();
      setShowOptions(true);
    }
  };

  return (
    <div className={styles.buttonContainer}>
      <Tooltip
        shouldShow={props.shouldShowTooltips}
        content={props.translations.toolbarAlign}
      >
        <button
          type="button"
          ref={node => {
            setPopperSourceElem(node);
            if (ref && 'current' in ref) {
              ref.current = node;
            }
          }}
          className={classnames(styles.button, {
            [styles.hovered]: stylesContext.hovered,
            [styles.focused]: stylesContext.focused,
            [styles.error]: stylesContext.error,
          })}
          onClick={onClickHandler}
          data-testid={TestIds.alignButton}
          aria-haspopup="true"
          aria-expanded={showOptions}
          aria-controls={`alignMenu_${rootId}`}
          tabIndex={tabIndex}
          onKeyDown={onKeyDownMenu}
          aria-label={`${translations.toolbarAlign}: ${alignSelected[activeAlignment]}`}
        >
          <div
            className={classnames(
              styles.buttonContent,
              formatClassNames(semanticClassNames.icons),
            )}
          >
            <AlignButton tabIndex={-1}>
              {alignmentButtons[activeAlignment]}
            </AlignButton>
          </div>
        </button>
      </Tooltip>
      <div
        ref={node => setPopperTargetElem(node)} // directly passing setPopperTargetElem doesn't work
        style={{
          ...stylesPopper.popper,
          visibility: showOptions ? 'visible' : 'hidden',
          zIndex: '1',
        }}
        {...attributes.popper}
        role="menu"
        id={`alignMenu_${rootId}`}
        onKeyDown={onKeyDown}
        onBlur={handleBlur}
      >
        <div
          ref={menuRef}
          className={classnames(styles.menuContainer, {
            [styles.hovered]: stylesContext.hovered,
            [styles.focused]: stylesContext.focused,
            [styles.error]: stylesContext.error,
          })}
        >
          {alignTypes.map(alignType => {
            return (
              <Tooltip
                shouldShow={props.shouldShowTooltips}
                content={alignActions[alignType]}
                key={`tooltip_text_align_${alignType}`}
                position="right"
              >
                <button
                  onClick={e => changeTextAlign(e, alignType)}
                  data-testid={`${TestIds.alignButton}-${alignType}`}
                  aria-label={alignActions[alignType]}
                  ref={
                    alignButtonRefs.current[alignType] ||
                    (alignType === 'left' ? leftAlignRef : undefined)
                  }
                  role="menuitemradio"
                  aria-checked={activeAlignment === alignType}
                  tabIndex={-1}
                  onBlur={handleBlur}
                >
                  <AlignButton
                    selected={activeAlignment === alignType}
                    onClick={e => changeTextAlign(e, alignType)}
                  >
                    {alignmentButtons[alignType]}
                  </AlignButton>
                </button>
              </Tooltip>
            );
          })}
        </div>
      </div>
    </div>
  );
});

export default StaticTextAlignButton;
